// Scope section — only scope endpoints. Section isolation rule
// (eslint-no-restricted-imports): nothing outside sections/scope/ imports this.
import { api, uploadToS3 } from "@/core/api/client";
import type {
  ActionItem,
  CarveoutDecisionRequest,
  CreateReportRequest,
  CreateReportResponse,
  Report,
  ScopeCheck,
  UpdateScopeCheckRequest,
} from "@/core/api/types";

export async function listReports(): Promise<Report[]> {
  const data = await api.get<{ reports: Report[] }>("/reports");
  return data.reports;
}

export async function getReport(id: string): Promise<{ report: Report; scope_checks: ScopeCheck[] }> {
  return api.get<{ report: Report; scope_checks: ScopeCheck[] }>(`/reports/${id}`);
}

export async function createReportAndUpload(
  req: CreateReportRequest,
  file: File
): Promise<{ report_id: string }> {
  const presign = await api.post<CreateReportResponse>("/reports", req);
  await uploadToS3(presign.upload_url, presign.upload_fields, file);
  return { report_id: presign.report_id };
}

/** #C6: upload a SOC 1 PDF INTO an existing 'awaiting_upload' placeholder report
 *  (e.g. a carve-out's requested SOC 1). Flips it to 'uploading' → ingest runs.
 *  The standard upload modal also passes the report name + cover FY the user set. */
export async function uploadToExistingReport(
  reportId: string,
  file: File,
  opts?: { service_org_name?: string; fiscal_year?: string }
): Promise<void> {
  const presign = await api.post<{ upload_url: string; upload_fields: Record<string, string> }>(
    `/reports/${reportId}/upload-url`,
    opts ?? {}
  );
  await uploadToS3(presign.upload_url, presign.upload_fields, file);
}

export async function updateScopeCheck(
  scopeCheckId: string,
  req: UpdateScopeCheckRequest
): Promise<{ scope_check: ScopeCheck; action_item: ActionItem | null }> {
  return api.patch<{ scope_check: ScopeCheck; action_item: ActionItem | null }>(
    `/scope-checks/${scopeCheckId}`,
    req
  );
}

export async function deleteReport(reportId: string): Promise<void> {
  await api.delete<{ status: string }>(`/reports/${reportId}`);
}

export async function decideCarveout(
  scopeCheckId: string,
  req: CarveoutDecisionRequest
): Promise<{ action_item: ActionItem | null }> {
  return api.post<{ action_item: ActionItem | null }>(`/scope-checks/${scopeCheckId}/carveouts`, req);
}

export async function addCarveout(
  scopeCheckId: string,
  req: { name: string; treatment?: string; summary?: string | null }
): Promise<void> {
  await api.post<{ action_item: ActionItem | null }>(`/scope-checks/${scopeCheckId}/carveouts/manual`, req);
}

export async function editCarveout(
  scopeCheckId: string,
  req: { carveout_id: string; name: string; treatment?: string; summary?: string | null }
): Promise<void> {
  await api.patch<{ status: string }>(`/scope-checks/${scopeCheckId}/carveouts/manual`, req);
}

export async function deleteCarveout(scopeCheckId: string, carveoutId: string): Promise<void> {
  await api.delete<{ deleted: boolean }>(
    `/scope-checks/${scopeCheckId}/carveouts/manual/${encodeURIComponent(carveoutId)}`
  );
}
